import React, { FC, useEffect, useState } from 'react';
import classNames from 'classnames';
import type { PieceType } from '@whitesharx/starfall-frontend-chessground';
import { Avatar, Flag, sounds, TimeFormats, Timer } from '@whitesharx/starfall-frontend-ui-kit';

import {
  containerStyles,
  infoBarStyles,
  playerInfoStyles,
  playerBarFlexStyles,
  infoBarFlexStyles,
  containerAlignStyles,
  playerInfoAlignStyles,
  nameStyles,
  avatarContainerStyles,
  flagContainerStyles,
  emptyFiguresStyles,
  emojiPositionStyles,
  emojiContainerStyles,
  timerContainerStyles,
  pulseBorderStyles,
  scaleContainerStyles,
  dangerStyles,
} from './styles.css';

import { Emoji, PlayerColor } from '../../types';
import { EmojiSelectProps } from './EmojiSelect/EmojiSelect';
import EmojiPopup from './EmojiPopup';
import EmojiButton from './EmojiButton';
import FiguresBar from '../FiguresBar';
import { Figures } from '../FiguresBar/FiguresBar';

export type EmojiOptions = {
  playerEmoji?: Emoji;
  enemyEmoji?: Emoji;
  onSend?: EmojiSelectProps['onChoose'];
};

type GamePlayerBarProps = {
  reactOnLittleTime?: boolean;
  pause?: boolean;
  leftTime?: number;
  pulse?: boolean;
  className?: string;
  playerName?: string;
  country?: string;
  setName?: string;
  avatarSrc?: string;
  color?: PlayerColor;
  figures?: Figures;
  figuresSetName?: string;
  isPlayer?: boolean;
  emojiOptions?: EmojiOptions;
  isDesktop: boolean;
  onEnd?: () => void;
};

const GamePlayerBar: FC<GamePlayerBarProps> = ({
  pulse,
  leftTime,
  pause,
  className,
  playerName,
  country,
  avatarSrc,
  setName,
  color,
  figures,
  figuresSetName,
  isPlayer,
  emojiOptions,
  isDesktop,
  reactOnLittleTime,
  onEnd,
}) => {
  const downedFiguresCount = Object.keys(figures || {}).reduce(
    (acc, next) => acc + (figures ? figures[next as PieceType] : 0),
    0
  );

  const [timerDangerStyles, setTimerDangerStyles] = useState<string | undefined>();
  const [littleTimeRemaining, setLittleTimeRemaining] = useState(false);

  useEffect(() => {
    if (reactOnLittleTime) {
      sounds.load(['clock']);
    }
  }, [reactOnLittleTime]);

  const onLittleTimeRemaining = reactOnLittleTime
    ? () => {
        sounds.play('clock');

        setLittleTimeRemaining(true);
        setTimerDangerStyles(dangerStyles);
        setTimeout(setTimerDangerStyles, 500);
      }
    : undefined;

  const emoji = isPlayer ? emojiOptions?.playerEmoji : emojiOptions?.enemyEmoji;

  return (
    <div className={classNames(containerStyles, className, { [containerAlignStyles]: isPlayer })}>
      <div className={playerBarFlexStyles[isPlayer ? 'reverse' : 'default']}>
        <div className={emojiContainerStyles}>
          {isPlayer && emojiOptions?.onSend && <EmojiButton onSend={emojiOptions.onSend} />}
          {emoji && (
            <EmojiPopup
              data={emoji}
              isPlayer={!!isPlayer}
              className={emojiPositionStyles[isPlayer ? 'top' : 'bottom']}
              isDesktop={isDesktop}
            />
          )}
          <div className={avatarContainerStyles}>
            <Avatar src={avatarSrc} size={'100%'} frame={setName} />
          </div>
        </div>
        <div className={classNames(infoBarStyles, infoBarFlexStyles[isPlayer ? 'reverse' : 'default'])}>
          <div
            className={classNames(playerInfoStyles, {
              [playerInfoAlignStyles]: isPlayer,
            })}
          >
            <div className={nameStyles}>{playerName}</div>
            {country && (
              <div className={flagContainerStyles}>
                <Flag country={country} size={21} />
              </div>
            )}
          </div>
          {downedFiguresCount > 0 && figures && color ? (
            <FiguresBar figures={figures} color={color} reverse={isPlayer} setName={figuresSetName} />
          ) : (
            <div className={emptyFiguresStyles} />
          )}
        </div>
      </div>
      {typeof leftTime === 'number' && (
        <div
          className={classNames(timerContainerStyles, {
            [pulseBorderStyles]: !littleTimeRemaining && pulse,
            pulse: !littleTimeRemaining && pulse,
          })}
        >
          <div className={classNames(scaleContainerStyles, timerDangerStyles)}>
            <Timer
              leftMilliseconds={leftTime}
              pause={pause}
              onLittleTimeRemaining={onLittleTimeRemaining}
              format={TimeFormats.MM_SS}
              onEnd={onEnd}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default GamePlayerBar;
